import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import './NfcRead.css';
import coinIcon from '../assets/icon/coinIcon.png';
import userIcon from '../assets/icon/userIcon.png';
import tarotBackSide from '../assets/picture/tarotCards/000.png';
import ichingBackSide from '../assets/picture/IChingCard.png';
import card_dict from '../assets/card_dict.js';
import { getToken, getUsername, isCurrentTokenExpired } from '../utils/auth';
import { getUserInfo } from '../functions/index';
import { getCardConfig } from '../utils/cardConfig';
import { useLanguage } from '../contexts/LanguageContext';
import { checkDailyRewardEligibility, claimDailyReward } from '../utils/dailyReward';
import DailyRewardModal from '../components/DailyRewardModal';

// Load Tarot images context
const tarotImagesContext = require.context('../assets/picture/tarotCards', false, /\.png$/);
const tarotImageMap: { [key: string]: string } = {};

tarotImagesContext.keys().forEach((item: string) => {
    const cardId = item.replace('./', '').replace('.png', '');
    if (cardId !== '000') {
        tarotImageMap[cardId] = tarotImagesContext(item);
    }
});

// Load IChing images context
const ichingImagesContext = require.context('../assets/picture/IChing', false, /\.png$/);
const ichingImageMap: { [key: string]: string } = {};

ichingImagesContext.keys().forEach((item: string) => {
    const cardId = item.replace('./', '').replace('.png', '');
    if (cardId !== '000') {
        ichingImageMap[cardId] = ichingImagesContext(item);
    }
});

const NfcRead = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    // 1. Determine configuration
    const pathSegments = location.pathname.split('/');
    const findConfig = () => {
        for (const segment of pathSegments) {
            const config = getCardConfig(segment);
            if (config) return config;
        }
        return getCardConfig('001')!; // Default to Tarot
    };
    const currentConfig = findConfig();

    const [status, setStatus] = useState<'scanning' | 'sliding' | 'flipping' | 'result'>('scanning');
    const [cardId, setCardId] = useState<string>('001');
    const [cardName, setCardName] = useState<string>('');
    const [cardPosition, setCardPosition] = useState<string>('');
    const [isReversed, setIsReversed] = useState(false);
    const [coinBalance, setCoinBalance] = useState<number>(0);
    const [username, setUsername] = useState<string>('');
    const [showDailyReward, setShowDailyReward] = useState(false);
    const [currentUserInfo, setCurrentUserInfo] = useState<any>(null);

    // Check if user is logged in
    const isLoggedIn = !!getToken() && !isCurrentTokenExpired();

    const { language, setLanguage, t, showLanguageModal, setShowLanguageModal } = useLanguage();

    useEffect(() => {
        const id = searchParams.get('id');
        const dictKey = currentConfig.id;

        // Image map selection based on ID
        const selectedImageMap = currentConfig.id === '001' ? tarotImageMap : ichingImageMap;

        const dict = (card_dict as any)[dictKey] || {};
        const availableIds = Object.keys(dict);

        let finalId = currentConfig.id === '001' ? '001' : '01'; // Fallback

        if (availableIds.length > 0) {
            const randomId = availableIds[Math.floor(Math.random() * availableIds.length)];
            // Use ID if provided and valid, otherwise random
            finalId = (id && dict[id]) ? id : randomId;
        }

        setCardId(finalId);
        setCardName(dict[finalId] || 'Unknown');
        const positionParam = searchParams.get('position') || '';
        setCardPosition(positionParam);
        if (currentConfig.hasReversed) {
            setIsReversed(positionParam === '1');
        }

        // Animation Sequence
        const scanTimer = setTimeout(() => {
            setStatus('sliding');
        }, 2000);

        const slideTimer = setTimeout(() => {
            setStatus('flipping');
        }, 3000);

        const flipTimer = setTimeout(() => {
            setStatus('result');
        }, 3800);

        return () => {
            clearTimeout(scanTimer);
            clearTimeout(slideTimer);
            clearTimeout(flipTimer);
        };
    }, [searchParams, currentConfig]);

    // Fetch user info and check daily reward eligibility
    useEffect(() => {
        const fetchUserInfo = async () => {
            const token = getToken();
            if (token && !isCurrentTokenExpired()) {
                try {
                    const userInfo = await getUserInfo(token);
                    if (userInfo) {
                        if (typeof userInfo.coin === 'number') setCoinBalance(userInfo.coin);
                        if (userInfo.username) setUsername(userInfo.username);
                        setCurrentUserInfo(userInfo);

                        // Check daily reward eligibility (uses localStorage)
                        const eligibility = checkDailyRewardEligibility();
                        if (eligibility.eligible) {
                            // Show the popup for user to claim
                            setShowDailyReward(true);
                        }
                    }
                } catch (error) {
                    console.error('Failed to fetch user info:', error);
                }
            }
        };
        fetchUserInfo();
    }, []);

    // Determine card image
    const activeImageMap = currentConfig.id === '001' ? tarotImageMap : ichingImageMap;
    const backInternal = currentConfig.id === '001' ? tarotBackSide : ichingBackSide;
    const cardImage = activeImageMap[cardId] || backInternal;

    const handleCardClick = () => {
        if (status === 'result' && currentConfig.hasReversed) {
            setIsReversed(!isReversed);
        }
    };

    const handleConfirm = () => {
        const cardData = [{
            cardId: cardId,
            cardName: cardName,
            ...(currentConfig.hasReversed ? {
                position: isReversed ? 'reversed' : 'upright',
                positionValue: isReversed ? 1 : 0
            } : {})
        }];

        navigate(`/nfc/${currentConfig.type}/chat`, {
            state: {
                spreadType: 'one',
                readingType: 'standard',
                cards: cardData
            }
        });
    };

    // Handle daily reward claim
    const handleClaimDailyReward = async () => {
        const token = getToken();
        if (!token || !currentUserInfo?.username) {
            return;
        }

        const result = await claimDailyReward(token, currentUserInfo.username);
        if (result.rewarded) {
            if (typeof result.newBalance === 'number') {
                setCoinBalance(result.newBalance);
            } else {
                setCoinBalance(prev => prev + 100);
            }
        }
    };

    return (
        <div className="nfc-page">
            {/* Header */}
            <div className="nfc-header">
                <div className="nfc-pill left coin-balance">
                    <img src={coinIcon} alt="Coin" />
                    <span>{coinBalance}</span>
                </div>
                <div className="header-right-group">
                    <button className="nfc-pill language-btn" onClick={() => setShowLanguageModal(true)}>
                        <span className="lang-icon"><span className="lang-zh">文</span>/A</span>
                    </button>
                    {isLoggedIn && (
                        <button className="nfc-pill right user-logged-in" onClick={() => navigate('/userCenter')}>
                            <img src={userIcon} alt="User" />
                            {username && <span>{username}</span>}
                        </button>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="nfc-content">
                {status === 'scanning' && (
                    <div className="scanning-ui">
                        <div className="scan-card-icon">
                            <div className="scan-card-outline">
                                <div className="scan-sparkle">✦</div>
                            </div>
                            <div className="scan-card-outline offset-1"></div>
                            <div className="scan-card-outline offset-2"></div>
                        </div>
                        <p className="scan-text">
                            {t.scanText1}<br />
                            {t.scanText2}
                        </p>
                    </div>
                )}

                {/* Animated Card */}
                <div
                    className={`animating-card-container ${status} ${isReversed ? 'reversed' : ''}`}
                    onClick={handleCardClick}
                >
                    <div className="animating-card-inner">
                        <div className="card-face card-back">
                            <img src={backInternal} alt="Back" />
                        </div>
                        <div className="card-face card-front">
                            <img src={cardImage} alt={cardName} />
                        </div>
                    </div>
                </div>

                {status === 'result' && (
                    <>

                        <div className="result-text fade-in">
                            {currentConfig.hasReversed && (
                                <div className={`card-status ${isReversed ? 'reversed' : 'upright'}`}>
                                    {isReversed ? t.reversed : t.upright}
                                </div>
                            )}
                        </div>

                        <div className="result-button fade-in">
                            {currentConfig.hasReversed && (
                                <p className="toggle-instruction">{t.tapToToggle}</p>
                            )}
                            <button className="confirm-btn" onClick={handleConfirm}>
                                {t.confirmButton}
                            </button>
                        </div>

                    </>)}
            </div>

            {/* Language Selection Modal */}
            {showLanguageModal && (
                <div className="language-modal-overlay">
                    <div className="language-modal-content">
                        <h2>{t.selectLanguage} / {language === 'en' ? '选择语言' : 'Select Language'}</h2>
                        <div className="language-options">
                            <button onClick={() => { setLanguage('en'); setShowLanguageModal(false); }}>
                                English
                            </button>
                            <button onClick={() => { setLanguage('zh'); setShowLanguageModal(false); }}>
                                中文
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Daily Reward Modal */}
            <DailyRewardModal
                isVisible={showDailyReward}
                onClaim={handleClaimDailyReward}
                onClose={() => setShowDailyReward(false)}
            />
        </div>
    );
};

export default NfcRead;
